/**
 * Request an idle callback or fallback to setTimeout
 * @returns {function} The requestIdleCallback function
 */
export const requestIdleCallback =
  typeof window.requestIdleCallback == 'function' ? window.requestIdleCallback : setTimeout;

/**
 * Check if the browser supports View Transitions API
 * @returns {boolean} True if the browser supports View Transitions API, false otherwise
 */
export function supportsViewTransitions() {
  return typeof document.startViewTransition === 'function';
}

/**
 * The current view transition
 * @type {{ current: Promise<void> | undefined }}
 */
export const viewTransition = {
  current: undefined,
};

/**
 * Determines which grid items should be animated during a transition.
 * It makes an estimation based on the zoom-out card size because it's
 * the common denominator for both transition states. I.e. transitioning either
 * from 10 to 20 cards the other way around, both need 20 cards to be animated.
 * @param {Element} grid - The grid element
 * @param {Element[]} cards - The cards to animate
 * @returns {number} - Number of cards that should be animated
 */
function getCardsToAnimate(grid, cards) {
  if (!grid || !cards || cards.length === 0) return 0;

  const itemSample = cards[0];
  if (!itemSample) return 0;

  // Calculate the visible area of the grid for the Y axis. Assume X is always fully visible:
  const gridRect = grid.getBoundingClientRect();
  const visibleArea = {
    top: Math.max(0, gridRect.top),
    bottom: Math.min(window.innerHeight, gridRect.bottom),
  };

  const visibleHeight = Math.round(visibleArea.bottom - visibleArea.top);
  if (visibleHeight <= 0) return 0;

  /** @type {import('product-card').ProductCard | null} */
  const cardSample = itemSample.querySelector('product-card');
  const gridStyle = getComputedStyle(grid);

  const galleryAspectRatio = cardSample?.refs?.cardImage?.style.getPropertyValue('--ratio') || '';
  let aspectRatio = parseFloat(galleryAspectRatio) || 0.5;
  if (galleryAspectRatio?.includes('/')) {
    const [width = '1', height = '2'] = galleryAspectRatio.split('/');
    aspectRatio = parseInt(width, 10) / parseInt(height, 10);
  }
  const cardGap = 16;
  const gridGap = parseInt(gridStyle.getPropertyValue('--columns-gap')) || 20;

  // Assume only a couple of lines of text in the card details (title and price).
  // If the title wraps into more lines, we might just animate more cards, but that's fine.
  const detailsSize = ((parseInt(gridStyle.fontSize) || 16) + 2) * 2;

  const isMobile = window.innerWidth < 767;

  // Always use the zoom-out state card width
  const cardWidth = isMobile ? Math.round((gridRect.width - gridGap) / 2) : 100;
  const cardHeight = Math.round(cardWidth / aspectRatio) + cardGap + detailsSize;

  // Calculate the number of cards that fit in the visible area:
  // - The width estimation is pretty accurate, we can ignore decimals.
  // - The height estimation needs to account for peeking rows, so we round up.
  const columnsInGrid = isMobile ? 2 : Math.floor((gridRect.width + gridGap) / (cardWidth + gridGap));
  const rowsInGrid = Math.ceil((visibleHeight - gridGap) / (cardHeight + gridGap));

  return columnsInGrid * rowsInGrid;
}

/**
 * Functions to run when a view transition of a given type is started
 * @type {{ [key: string]: () => Promise<(() => void) | undefined> }}
 */
const viewTransitionTypes = {
  'product-grid': async () => {
    const grid = document.querySelector('.product-grid');
    const productCards = /** @type {HTMLElement[]} */ ([
      ...document.querySelectorAll('.product-grid .product-grid__item'),
    ]);

    if (!grid || !productCards.length) return;

    // Lift the visible sticky header into its own view-transition group so it
    // stays above the per-card snapshots. Cards get their own names below,
    // which paints them above the `root` snapshot (where the header lives) —
    // without this the grid would render over the sticky header mid-transition.
    const stickyHeader = /** @type {HTMLElement | undefined} */ (
      [...document.querySelectorAll('.header-section.header-sticky')].find(
        (el) => el instanceof HTMLElement && el.offsetParent !== null
      )
    );
    stickyHeader?.style.setProperty('view-transition-name', 'sticky-header');

    await new Promise((resolve) =>
      requestAnimationFrame(() => {
        const cardsToAnimate = getCardsToAnimate(grid, productCards);

        productCards.forEach((card, index) => {
          if (index < cardsToAnimate) {
            if (card.dataset.productId) {
              card.style.setProperty('view-transition-name', `product-card-${card.dataset.productId}`);
            }
          } else {
            card.style.setProperty('content-visibility', 'hidden');
          }
        });

        resolve(null);
      })
    );

    return () => {
      stickyHeader?.style.removeProperty('view-transition-name');
      productCards.forEach((card) => {
        card.style.removeProperty('view-transition-name');
        card.style.removeProperty('content-visibility');
      });
    };
  }
};

/**
 * Low-power device heuristic based on CPU cores and RAM.
 * @returns {boolean}
 */
export function isLowPowerDevice() {
  return Number(navigator.hardwareConcurrency) <= 2 || Number(navigator.deviceMemory) <= 2;
}

/**
 * Detect in-app WebViews (Android System WebView, Facebook, Instagram, TikTok)
 * known to freeze or white-screen during view transitions.
 * @param {string} [userAgent=navigator.userAgent] - User-agent string to test.
 * @returns {boolean}
 */
export function shouldDisableCrossDocumentViewTransitions(userAgent = navigator.userAgent) {
  const ua = userAgent || '';
  const androidWebView = /\bAndroid\b/i.test(ua) && /;\s?wv\)/i.test(ua);
  const knownInAppBrowser =
    /\b(FBAN|FBAV|FB_IAB|FBIOS|Instagram|musical_ly|Bytedance|BytedanceWebview|trill|TikTok)(?:\b|_)/i.test(ua);
  return androidWebView || knownInAppBrowser;
}

/**
 * Starts a view transition
 * @param {() => void} callback The callback to call when the view transition starts
 * @param {string[]} [types] The types of view transition to use
 * @returns {Promise<void>} A promise that resolves when the view transition finishes
 */
export function startViewTransition(callback, types) {
  // eslint-disable-next-line no-async-promise-executor
  return new Promise(async (resolve) => {
    if (
      supportsViewTransitions() &&
      !prefersReducedMotion() &&
      !isLowPowerDevice() &&
      !shouldDisableCrossDocumentViewTransitions()
    ) {
      let cleanupFunctions = [];

      if (types) {
        for (const type of types) {
          if (viewTransitionTypes[type]) {
            const cleanupFunction = await viewTransitionTypes[type]();
            if (cleanupFunction) cleanupFunctions.push(cleanupFunction);
          }
        }
      }

      const transition = document.startViewTransition(callback);

      if (!viewTransition.current) {
        viewTransition.current = transition.finished;
      }

      if (types) types.forEach((type) => transition.types.add(type));

      transition.finished.then(() => {
        viewTransition.current = undefined;
        cleanupFunctions.forEach((cleanupFunction) => cleanupFunction());
        resolve();
      });

      return;
    }

    // Fallback for browsers that don't support this API yet
    callback();
    resolve();
  });
}


/**
 * Executes a callback in a separate task after the next frame.
 * Using to defer non-critical tasks until after the interaction is complete.
 * @see https://web.dev/articles/optimize-inp#yield_to_allow_rendering_work_to_occur_sooner
 * @param {() => any} callback - The callback to execute
 */
export const requestYieldCallback = (callback) => {
  requestAnimationFrame(() => {
    setTimeout(callback, 0);
  });
};

/**
 * Detect iOS (including iPad on iOS 13+ which reports as Mac).
 * Used to fix script execution on iOS 16.1 and similar.
 * @returns {boolean}
 */
export function isIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/**
 * Re-executes script tags inside element for iOS 16.1 and similar.
 * Inline non-module: eval in global scope then remove; external: append to document.head then remove.
 * @param {Element} element
 */
function executeScriptsInElementForIOS(element) {
  const scripts = Array.from(element.querySelectorAll('script'));
  scripts.forEach((oldScriptTag) => {
    const hasSrc = !!oldScriptTag.src;
    const isModule = oldScriptTag.type === 'module';
    if (!hasSrc && !isModule) {
      const code = oldScriptTag.innerHTML;
      if (code.trim()) {
        try {
          (0, eval)(code);
        } catch (_) {}
      }
      oldScriptTag.parentNode?.removeChild(oldScriptTag);
      return;
    }
    if (hasSrc) {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attr) => {
        newScriptTag.setAttribute(attr.name, attr.value);
      });
      document.head.appendChild(newScriptTag);
      oldScriptTag.parentNode?.removeChild(oldScriptTag);
    }
  });
}

/**
 * Re-executes script tags that are already inside the element.
 * (Scripts inserted via cloneNode/innerHTML don't run by default.)
 * On iOS: uses executeScriptsInElementForIOS; otherwise replaceChild as before.
 * @param {Element} element
 */
export function executeScriptsInElement(element) {
  if (isIOS()) {
    executeScriptsInElementForIOS(element);
    return;
  }
  element.querySelectorAll('script').forEach((oldScriptTag) => {
    const newScriptTag = document.createElement('script');
    Array.from(oldScriptTag.attributes).forEach((attribute) => {
      newScriptTag.setAttribute(attribute.name, attribute.value);
    });
    newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
    oldScriptTag.parentNode?.replaceChild(newScriptTag, oldScriptTag);
  });
}

/**
 * Sets innerHTML and reinjects script tags so they execute.
 * (Scripts inserted via innerHTML don't run by default.)
 * @param {Element} element
 * @param {string} html
 */
export function setInnerHTMLAndExecuteScripts(element, html) {
  element.innerHTML = html;
  executeScriptsInElement(element);
}

/**
 * @typedef {{ [key: string]: string | undefined }} Headers
 */

/**
 * @typedef {Object} FetchConfig
 * @property {string} method
 * @property {Headers} headers
 * @property {string | FormData | undefined} [body]
 */

/**
 * Creates a fetch configuration object
 * @param {string} [type] The type of response to expect
 * @param {Object} [config] The config of the request
 * @param {FetchConfig['body']} [config.body] The body of the request
 * @param {FetchConfig['headers']} [config.headers] The headers of the request
 * @returns {RequestInit} The fetch configuration object
 */
export function fetchConfig(type = 'json', config = {}) {
  /** @type {Headers} */
  const headers = { 'Content-Type': 'application/json', Accept: `application/${type}`, ...config.headers };

  if (type === 'javascript') {
    headers['X-Requested-With'] = 'XMLHttpRequest';
    delete headers['Content-Type'];
  }

  return {
    method: 'POST',
    headers: /** @type {HeadersInit} */ (headers),
    body: config.body,
  };
}

/**
 * Creates a debounced function that delays calling the provided function (fn)
 * until after wait milliseconds have elapsed since the last time
 * the debounced function was invoked. The returned function has a .cancel()
 * method to cancel any pending calls.
 *
 * @template {(...args: any[]) => any} T
 * @param {T} fn The function to debounce
 * @param {number} wait The time (in milliseconds) to wait before calling fn
 * @returns {T & { cancel(): void }} A debounced version of fn with a .cancel() method
 */
export function debounce(fn, wait) {
  /** @type {number | undefined} */
  let timeout;

  /** @param {...any} args */
  function debounced(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  }

  // Add the .cancel method:
  debounced.cancel = () => {
    clearTimeout(timeout);
  };

  return /** @type {T & { cancel(): void }} */ (debounced);
}

/**
 * Throttle using requestAnimationFrame (Concept theme style).
 * Runs at most once per frame, with trailing call using latest args.
 *
 * @template {(...args: any[]) => any} T
 * @param {T} fn The function to throttle
 * @returns {T & { cancel(): void }} A throttled version of fn with a .cancel() method
 */
export function throttle(fn) {
  let requestId = null;
  let lastArgs = null;

  /** @param {...any} args */
  function throttled(...args) {
    lastArgs = args;
    if (requestId === null) {
      requestId = requestAnimationFrame(() => {
        requestId = null;
        fn.apply(this, lastArgs);
      });
    }
  }

  throttled.cancel = () => {
    if (requestId !== null) {
      cancelAnimationFrame(requestId);
      requestId = null;
    }
  };

  return /** @type {T & { cancel(): void }} */ (throttled);
}

/**
 * A media query for reduced motion
 * @type {MediaQueryList}
 */
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

/**
 * Check if the user prefers reduced motion
 * @returns {boolean} True if the user prefers reduced motion, false otherwise
 */
export function prefersReducedMotion() {
  return reducedMotion.matches;
}

/**
 * Normalize a string
 * @param {string} str The string to normalize
 * @returns {string} The normalized string
 */
export function normalizeString(str) {
  return str
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

/**
 * Format a money value
 * @param {string} value The value to format
 * @returns {string} The formatted value
 */
export function formatMoney(value) {
  let valueWithNoSpaces = value.replace(' ', '');
  if (valueWithNoSpaces.indexOf(',') === -1) return valueWithNoSpaces;
  if (valueWithNoSpaces.indexOf(',') < valueWithNoSpaces.indexOf('.')) return valueWithNoSpaces.replace(',', '');
  if (valueWithNoSpaces.indexOf('.') < valueWithNoSpaces.indexOf(','))
    return valueWithNoSpaces.replace('.', '').replace(',', '.');
  if (valueWithNoSpaces.indexOf(',') !== -1) return valueWithNoSpaces.replace(',', '.');

  return valueWithNoSpaces;
}

/**
 * Formats an amount in cents into a money string, honoring the shop money format's amount
 * placeholder variant (no_decimals / comma_separator / apostrophe_separator).
 * @param {number} cents The amount in cents.
 * @param {string} [format] The Liquid money format, defaults to the shop money format.
 * @returns {string}
 */
export function moneyFromCents(cents, format = window.Shopify?.money_format ?? '${{amount}}') {
  const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);
  if (!placeholder) return format;
  const name = placeholder[1];
  const decimals = name.includes('no_decimals') ? 0 : 2;
  const comma = name.includes('comma_separator');
  const thousands = name.includes('apostrophe_separator') ? "'" : comma ? '.' : ',';
  const [whole, fraction] = (Number(cents) / 100).toFixed(decimals).split('.');
  let amount = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
  if (fraction) amount += (comma ? ',' : '.') + fraction;
  return format.replace(placeholder[0], amount);
}


/**
 * Check if the document is ready and call the callback when it is.
 * @param {() => void} callback The function to call when the document is ready.
 */
export function onDocumentReady(callback) {
  if (document.readyState === 'complete') {
    callback();
  } else {
    window.addEventListener('load', callback);
  }
}


/**
 * Wait for DOM to be ready, then execute callback during browser idle time.
 * Best for non-critical lazy loading tasks that shouldn't block main thread.
 * @param {() => void} callback The function to call during idle time.
 */
export function onIdleAfterDOMReady(callback) {
  const executeWhenIdle = () => {
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(callback);
    } else {
      setTimeout(callback, 1);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', executeWhenIdle, { once: true });
  } else {
    // DOM already loaded, execute immediately during idle
    executeWhenIdle();
  }
}

/**
 * Runs a callback during the first idle period after all page resources have loaded
 * (window.load). Safer than onIdleAfterDOMReady for non-critical background work
 * because it avoids competing with LCP images and fonts over network bandwidth.
 * @param {Function} callback
 */
export function onIdleAfterPageLoad(callback) {
  const executeWhenIdle = () => {
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(callback);
    } else {
      setTimeout(callback, 1);
    }
  };

  if (document.readyState === 'complete') {
    executeWhenIdle();
  } else {
    window.addEventListener('load', executeWhenIdle, { once: true });
  }
}

/**
 * Wait for all animations to finish before calling the callback.
 * @param {Element | Element[]} elements The element(s) whose animations to wait for.
 * @param {() => void} [callback] The function to call when all animations are finished.
 * @param {Object} [options] The options to pass to `Element.getAnimations`.
 * @returns {Promise<void>} A promise that resolves when all animations are finished.
 */
export function onAnimationEnd(elements, callback, options = { subtree: true }) {
  const animations = Array.isArray(elements)
    ? elements.flatMap((element) => element.getAnimations(options))
    : elements.getAnimations(options);
  const animationPromises = animations.reduce((acc, animation) => {
    // Ignore ViewTimeline animations
    if (animation.timeline instanceof DocumentTimeline) {
      acc.push(animation.finished);
    }

    return acc;
  }, /** @type {Promise<Animation>[]} */ ([]));

  return Promise.allSettled(animationPromises).then(callback);
}

/**
 * Check if the click is outside the element.
 * @param {MouseEvent} event The mouse event.
 * @param {Element} element The element to check.
 * @returns {boolean} True if the click is outside the element, false otherwise.
 */
export function isClickedOutside(event, element) {
  if (event.target instanceof HTMLDialogElement || !(event.target instanceof Element)) {
    return !isPointWithinElement(event.clientX, event.clientY, element);
  }

  return !element.contains(event.target);
}

/**
 * Check if a point is within an element.
 * @param {number} x The x coordinate of the point.
 * @param {number} y The y coordinate of the point.
 * @param {Element} element The element to check.
 * @returns {boolean} True if the point is within the element, false otherwise.
 */
export function isPointWithinElement(x, y, element) {
  const { left, right, top, bottom } = element.getBoundingClientRect();

  return x >= left && x <= right && y >= top && y <= bottom;
}

/**
 * Places a hotspot's tooltip inside the given bounds: it shifts sideways near a vertical edge and
 * flips above the dot when there is no room below, so an image that clips its overflow can't cut
 * the card off. Falls back to a centred tooltip when it is wider than the bounds allow
 * @param {HTMLElement} hotspotEl - The hotspot carrying the tooltip
 * @param {HTMLElement} boundsEl - The element the tooltip has to stay inside
 */
export function positionHotspotTooltip(hotspotEl, boundsEl) {
  const tooltip = hotspotEl?.querySelector('.hotspot__tooltip');
  if (!tooltip || !boundsEl) return;

  const bounds = boundsEl.getBoundingClientRect();
  const spot = hotspotEl.getBoundingClientRect();
  const width = tooltip.offsetWidth;
  const half = width / 2;
  const center = spot.left + spot.width / 2;
  const edgeGap = 16;
  let shift = 0;

  if (width <= bounds.width - edgeGap * 2) {
    if (center - half < bounds.left + edgeGap) shift = bounds.left + edgeGap - (center - half);
    else if (center + half > bounds.right - edgeGap) shift = bounds.right - edgeGap - (center + half);
  }

  hotspotEl.style.setProperty('--hotspot-tooltip-shift', `${Math.round(shift)}px`);
  hotspotEl.style.setProperty('--hotspot-arrow-left', `${Math.round(Math.min(Math.max(half - shift, 14), width - 14))}px`);
  hotspotEl.classList.toggle('is-flipped', spot.bottom + tooltip.offsetHeight + 24 > bounds.bottom);
}

/**
 * Clamps a number between a minimum and maximum value.
 * @param {number} value - The input number to clamp.
 * @param {number} min - The minimum value.
 * @param {number} max - The maximum value.
 * @returns {number} The clamped value.
 */
export function clamp(value, min, max) {
  return Math.max(min, Math.min(value, max));
}

/**
 * Calculates the center point of an element along the specified axis.
 * @param {Element} element - The DOM element to find the center of.
 * @param {'x' | 'y'} [axis] - The axis ('x' or 'y') to get the center for. If not provided, returns both axes.
 * @template {('x' | 'y')} T
 * @param {T} [axis]
 * @returns {T extends ('x' | 'y') ? number : {x: number, y: number}} The center point along the axis or an object with x and y coordinates.
 */
export function center(element, axis) {
  const { left, width, top, height } = element.getBoundingClientRect();
  const point = {
    x: left + width / 2,
    y: top + height / 2,
  };

  if (axis) return /**  @type {any} */ (point[axis]);

  return /**  @type {any} */ (point);
}

/**
 * Calculates the start point of an element along the specified axis.
 * @param {Element} element - The DOM element to find the start of.
 * @param {'x' | 'y'} [axis] - The axis ('x' or 'y') to get the start for. If not provided, returns both axes.
 * @returns {number | {x: number, y: number}} The start point along the axis or an object with x and y coordinates.
 */
export function start(element, axis) {
  const { left, top } = element.getBoundingClientRect();
  const point = { x: left, y: top };

  if (axis) return /**  @type {any} */ (point[axis]);

  return /**  @type {any} */ (point);
}

/**
 * Finds the value in an array that is closest to a target value.
 * @param {number[]} values - An array of numbers.
 * @param {number} target - The target number to find the closest value to.
 * @returns {number} The value from the array closest to the target.
 */
export function closest(values, target) {
  return values.reduce(function (prev, curr) {
    return Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev;
  });
}

/**
 * Prevents the default action of an event.
 * @param {Event} event - The event to prevent the default action of.
 */
export function preventDefault(event) {
  event.preventDefault();
}

/**
 * Get the visible elements within a root element.
 * @template {Element} T
 * @param {Element} root - The element within which elements should be visible.
 * @param {T[] | undefined} elements - The elements to check for visibility.
 * @param {number} [ratio=1] - The minimum percentage of the element that must be visible.
 * @param {'x' | 'y'} [axis] - Whether to only check along 'x' axis, 'y' axis, or both if undefined.
 * @returns {T[]} An array containing the visible elements.
 */
export function getVisibleElements(root, elements, ratio = 1, axis) {
  if (!elements?.length) return [];
  const rootRect = root.getBoundingClientRect();

  return elements.filter((element) => {
    const { width, height, top, right, left, bottom } = element.getBoundingClientRect();

    if (ratio < 1) {
      const intersectionLeft = Math.max(rootRect.left, left);
      const intersectionRight = Math.min(rootRect.right, right);
      const intersectionWidth = Math.max(0, intersectionRight - intersectionLeft);

      if (axis === 'x') {
        return width > 0 && intersectionWidth / width >= ratio;
      }

      const intersectionTop = Math.max(rootRect.top, top);
      const intersectionBottom = Math.min(rootRect.bottom, bottom);
      const intersectionHeight = Math.max(0, intersectionBottom - intersectionTop);

      if (axis === 'y') {
        return height > 0 && intersectionHeight / height >= ratio;
      }

      const intersectionArea = intersectionWidth * intersectionHeight;
      const elementArea = width * height;

      // Check that at least the specified ratio of the element is visible
      return elementArea > 0 && intersectionArea / elementArea >= ratio;
    }

    const isWithinX = left >= rootRect.left && right <= rootRect.right;
    if (axis === 'x') {
      return isWithinX;
    }

    const isWithinY = top >= rootRect.top && bottom <= rootRect.bottom;
    if (axis === 'y') {
      return isWithinY;
    }

    return isWithinX && isWithinY;
  });
}

/**
 * Preloads an image
 * @param {string} src - The source of the image to preload
 */
export function preloadImage(src) {
  const image = new Image();
  image.src = src;
}


/**
 * A media query for mobile screens
 * @type {MediaQueryList}
 */
export const mediaQueryMobile = matchMedia('(max-width: 767px)');

/**
 * A media query for tablet screens
 * @type {MediaQueryList}
 */
export const mediaQueryTablet = matchMedia('(min-width: 768px) and (max-width: 1024px)');

/**
 * A media query for laptop screens
 * @type {MediaQueryList}
 */
export const mediaQueryLaptop = matchMedia('(min-width: 1025px) and (max-width: 1279px)');

/**
 * A media query for desktop screens
 * @type {MediaQueryList}
 */
export const mediaQueryDesktop = matchMedia('(min-width: 1025px)');

/**
 * Check if the current breakpoint is mobile
 * @returns {boolean} True if the current breakpoint is mobile, false otherwise
 */
export function isMobileBreakpoint() {
  return mediaQueryMobile.matches;
}

/**
 * On mobile, browsers evaluate srcset at parse time while a dialog is display:none,
 * causing the wrong image size to be selected. Resetting srcset after the dialog opens
 * forces re-evaluation against the actual layout dimensions.
 *
 * Safe with loading="lazy": no download starts before the reset, so only one correct
 * request is made.
 *
 * @param {Element} container - The dialog or container element whose images need resetting.
 */
export function resetSrcsetOnMobile(container) {
  if (!isMobileBreakpoint()) return;
  requestAnimationFrame(() => {
    container.querySelectorAll('img[srcset]').forEach((img) => {
      const srcset = img.srcset;
      img.srcset = '';
      img.srcset = srcset;
    });
  });
}

/**
 * Check if the current breakpoint is laptop (1025-1279)
 * @returns {boolean} True if the current breakpoint is laptop, false otherwise
 */
export function isLaptopBreakpoint() {
  return mediaQueryLaptop.matches;
}

/**
 * Check if the current breakpoint is desktop
 * @returns {boolean} True if the current breakpoint is desktop, false otherwise
 */
export function isDesktopBreakpoint() {
  return mediaQueryDesktop.matches;
}

/**
 * Cached active header section. Invalidated when the viewport crosses the mobile
 * breakpoint or the cached element is detached from the DOM (e.g. after section
 * re-render in design mode).
 * @type {HTMLElement | null}
 */
let _cachedActiveHeader = null;
/** @type {boolean | null} */
let _cachedActiveHeaderIsMobile = null;

/**
 * Returns the currently-rendered header section (desktop or mobile).
 *
 * Both `.header-desktop-section` and `.header-mobile-section` exist in the DOM at
 * the same time; only one is visible at any given viewport width, controlled by a
 * CSS `@media` query using the merchant-configured mobile breakpoint
 * (`data-mobile-breakpoint` on `.header__mobile`, exposed via `getHeaderMobileBreakpointWidth`).
 *
 * Result is cached per viewport mode to avoid `querySelector` on every call.
 *
 * @returns {HTMLElement | null} The active header section element, or null if none found.
 */
export function getActiveHeaderSection() {
  const isMobileViewport = window.innerWidth <= getHeaderMobileBreakpointWidth();

  if (_cachedActiveHeaderIsMobile === isMobileViewport && _cachedActiveHeader?.isConnected) {
    return _cachedActiveHeader;
  }

  const primarySelector = isMobileViewport ? '.header-mobile-section' : '.header-desktop-section';
  const fallbackSelector = isMobileViewport ? '.header-desktop-section' : '.header-mobile-section';
  _cachedActiveHeader = /** @type {HTMLElement | null} */ (
    document.querySelector(primarySelector) || document.querySelector(fallbackSelector)
  );
  _cachedActiveHeaderIsMobile = isMobileViewport;
  return _cachedActiveHeader;
}

/**
 * Calculate sticky offset (header height) for scroll positioning.
 *
 * Picks the active header section based on the merchant-configured mobile
 * breakpoint, then resolves the visible header height from its sticky mode.
 *
 * For modes whose header collapses/hides depending on direction
 * (`top_only`, `bottom_only`, `scroll_up`), the header is considered "expanded"
 * if EITHER:
 *   - the caller hints it will be scrolling up (`scrollingUp: true`), OR
 *   - the header already has `.header-pinning` (current state).
 *
 * This dual check covers both:
 *   - "scroll to a target" callers that know the future direction
 *     (e.g. `results-list#handleFilterUpdate`), so the destination won't be
 *     covered when the header pins mid-scroll;
 *   - "current state" callers that have no target (e.g. `sticky-content` RAF,
 *     active-slide detection), which can omit the hint and rely on
 *     `.header-pinning` as the source of truth.
 *
 * Resolved values per mode:
 *   - always:                                              --header-sticky-height
 *   - top_only / bottom_only + (scrollingUp OR pinning):   --header-height
 *   - top_only / bottom_only + (neither):                  --header-sticky-height
 *   - scroll_up + (scrollingUp OR pinning):                --header-height
 *   - scroll_up + (neither):                               0
 *
 * @param {Object} [options]
 * @param {boolean} [options.scrollingUp] - Optional hint that the page will scroll upward toward the target.
 * @returns {number} Sticky offset in pixels
 */
export function getStickyOffset({ scrollingUp = false } = {}) {
  const headerSection = getActiveHeaderSection();
  const rootStyles = getComputedStyle(document.documentElement);

  const readVar = /** @param {string} name */ (name) =>
    parseFloat(rootStyles.getPropertyValue(name)) || 0;

  if (!headerSection) return readVar('--header-sticky-height');

  const { classList } = headerSection;
  const willExpand = scrollingUp || classList.contains('header-pinning');

  if (classList.contains('header-sticky--scroll_up')) {
    return willExpand ? readVar('--header-height') : 0;
  }

  if (classList.contains('header-sticky--top_only') || classList.contains('header-sticky--bottom_only')) {
    return readVar(willExpand ? '--header-height' : '--header-sticky-height');
  }

  return readVar('--header-sticky-height');
}

/**
 * Check if the current breakpoint is desktop
 * @returns {boolean} True if the current breakpoint is desktop, false otherwise
 */
export function isTabletBreakpoint() {
  return mediaQueryTablet.matches;
}

/**
 * Cached mobile breakpoint width from the header mobile element.
 * @type {number | null}
 */
let _mobileBreakpointWidth = null;

/**
 * Gets the mobile breakpoint width from the header mobile element.
 * Result is cached after the first call — subsequent calls return immediately.
 * Falls back to 900 if the element is not found.
 * @returns {number} Mobile breakpoint width in pixels
 */
export function getHeaderMobileBreakpointWidth() {
  if (_mobileBreakpointWidth !== null) return _mobileBreakpointWidth;
  const headerMobile = document.querySelector('.header-mobile-section .header__mobile');
  _mobileBreakpointWidth = headerMobile ? parseInt(headerMobile.dataset.mobileBreakpoint) || 900 : 900;
  return _mobileBreakpointWidth;
}

export function isTouch() {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0;
}

/** True when the page is laid out right-to-left. */
export function isRTL() {
  return document.dir === 'rtl';
}

/**
 * Usage
  observerElement({
    element: this,
    callback: fn,
    threshold: 0,
  });
 */
export function observerElement(options) {
  const threshold = options.threshold ? options.threshold : 0;

  const observer = new IntersectionObserver(
    (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (typeof options.callback === "function") {
            options.callback();
            observer.unobserve(entry.target);
          }
        }
      });
    },
    { rootMargin: `${threshold}px 0px ${threshold}px 0px` }
  );

  observer.observe(options.element);
  return observer; // optional
}

/**
 * Waits for a specific event on a target and resolves with the event object.
 * @param {EventTarget} target - The element to listen on.
 * @param {string} eventName - The event to wait for.
 * @param {Object} [options] - Event listener options (e.g., { once: true, capture: false }).
 * @param {boolean} [strict=true] - If true, only resolve if event.target === target.
 * @param {number} [timeout] - Optional timeout in ms to reject if event doesn't fire.
 * @returns {Promise<Event>} Promise that resolves with the event object.
 */
export function waitForEvent(target, eventName, options = {}, strict = true, timeout) {
  return new Promise((resolve, reject) => {
    const eventHandler = (event) => {
      if (!strict || event.target === target) {
        target.removeEventListener(eventName, eventHandler, options);
        if (timeoutId) clearTimeout(timeoutId);
        resolve(event);
      }
    };
    target.addEventListener(eventName, eventHandler, options);

    let timeoutId;
    if (timeout) {
      timeoutId = setTimeout(() => {
        target.removeEventListener(eventName, eventHandler, options);
        reject(new Error(`waitForEvent: '${eventName}' timed out after ${timeout}ms`));
      }, timeout);
    }
  });
}

/**
 * Wait until media elements are ready (img, iframe, svg).
 * @param {HTMLElement | HTMLElement[] | NodeListOf<HTMLElement>} media
 * @returns {Promise<HTMLElement | HTMLElement[]>}
 * Usage:
 * const media = document.querySelectorAll("img, iframe, svg");
 * await waitForMedia(media);
 */
export function waitForMedia(media) {
  const elements = media instanceof HTMLElement
    ? [media]
    : Array.isArray(media)
    ? media
    : Array.from(media || []);

  const tasks = elements.map(el => {
    if (el instanceof HTMLImageElement) {
      // Already loaded?
      if (el.complete && el.naturalWidth > 0) return Promise.resolve(el);
      return new Promise((resolve, reject) => {
        el.addEventListener('load', () => resolve(el), { once: true });
        el.addEventListener('error', () => reject(new Error(`Image failed: ${el.src}`)), { once: true });
      });
    }

    if (el instanceof HTMLIFrameElement) {
      // Iframe fires load when its content is ready
      return new Promise(resolve => {
        if (el.contentDocument?.readyState === "complete") return resolve(el);
        el.addEventListener('load', () => resolve(el), { once: true });
      });
    }

    if (el instanceof SVGElement) {
      // Inline <svg> is already parsed/rendered once in DOM
      return Promise.resolve(el);
    }

    return Promise.resolve(el); // fallback for unknown element types
  });

  return elements.length === 1 ? tasks[0] : Promise.all(tasks);
}

/**
 * Gets a cookie value by name
 * @param {string} name - The cookie name
 * @returns {string | null} The cookie value or null if not found
 */
export function getCookie(name) {
  const match = document.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`);
  return match ? match[2] : null;
}

/**
 * Sets a cookie with expiration in days
 * @param {string} name - The cookie name
 * @param {string} value - The cookie value
 * @param {number} expiryDays - Number of days until expiration
 * @param {string} [path] - The cookie path (default: '/')
 */
export function setCookie(name, value, expiryDays, path = '/') {
  const maxAge = expiryDays * 24 * 60 * 60;
  document.cookie = `${name}=${value}; max-age=${maxAge}; path=${path}`;
}

/**
 * Removes a cookie by setting its max-age to 0
 * @param {string} name - The cookie name
 * @param {string} [path] - The cookie path (default: '/')
 */
export function removeCookie(name, path = '/') {
  document.cookie = `${name}=; max-age=0; path=${path}`;
}

/**
 * Gets a value from localStorage
 * Automatically checks expiration and removes expired items if the item was stored with expiryDays.
 * Items stored without expiryDays are returned as-is (stored permanently).
 * @param {string} key - The localStorage key
 * @returns {string | null} The value or null if not found or expired
 */
export function getLocalStorage(key) {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return null;

    try {
      const item = JSON.parse(stored);
      if (item.expiry !== undefined) {
        if (Date.now() > item.expiry) {
          localStorage.removeItem(key);
          return null;
        }
        return item.value;
      }
      return stored;
    } catch (e) {
      return stored;
    }
  } catch (e) {
    console.error('Error getting from localStorage:', e);
    return null;
  }
}

/**
 * Sets a value in localStorage
 * @param {string} key - The localStorage key
 * @param {string} value - The value to store
 * @param {number} [expiryDays] - Optional: Number of days until expiration
 */
export function setLocalStorage(key, value, expiryDays) {
  try {
    if (expiryDays) {
      const expiry = Date.now() + (expiryDays * 24 * 60 * 60 * 1000);
      localStorage.setItem(key, JSON.stringify({ value, expiry }));
    } else {
      localStorage.setItem(key, value);
    }
  } catch (e) {
    console.error('Error setting localStorage:', e);
  }
}

/**
 * Removes a value from localStorage
 * @param {string} key - The localStorage key
 */
export function removeLocalStorage(key) {
  try {
    localStorage.removeItem(key);
  } catch (e) {
    console.error('Error removing from localStorage:', e);
  }
}

/**
 * Scrolls to center an element horizontally within its scrollable container
 * Only scrolls horizontally, preserves vertical scroll position
 * @param {HTMLElement} container - The scrollable container element
 * @param {HTMLElement} targetElement - The element to scroll to
 * @param {Object} options - Options
 * @param {boolean} [options.smooth=true] - Use smooth scrolling
 */
export function scrollToCenterHorizontal(container, targetElement, { smooth = true } = {}) {
  if (!container || !targetElement) return;

  // Defer layout reads to next frame to avoid forced reflow when called
  // synchronously after a DOM/style mutation (e.g. class toggle, style change).
  requestAnimationFrame(() => {
    const scrollWidth = container.scrollWidth;
    const containerWidth = container.clientWidth;

    if (scrollWidth <= containerWidth) return;

    // Relative move: scrollLeft runs negative under RTL, and the browser clamps.
    const containerRect = container.getBoundingClientRect();
    const elementRect = targetElement.getBoundingClientRect();
    const delta =
      elementRect.left + elementRect.width / 2 - (containerRect.left + containerRect.width / 2);

    container.scrollTo({
      left: container.scrollLeft + delta,
      behavior: smooth ? 'smooth' : 'auto',
    });
  });
}

/**
 * Smooth-scrolls a horizontally-scrollable container so `targetElement` aligns to
 * the start, accounting for the container's `scroll-padding-inline`.
 * (Unlike {@link scrollToCenterHorizontal}, which centers the element.)
 * @param {HTMLElement | null} container
 * @param {HTMLElement} targetElement
 * @returns {boolean} True if scrolled, false if no container.
 */
export function scrollHorizontalToElement(container, targetElement) {
  if (!container) return false;

  const containerRect = container.getBoundingClientRect();
  const elementRect = targetElement.getBoundingClientRect();
  const scrollPadding = parseFloat(getComputedStyle(container).scrollPaddingInline) || 0;

  // The inline start is the right edge under RTL.
  const delta = isRTL()
    ? elementRect.right - (containerRect.right - scrollPadding)
    : elementRect.left - (containerRect.left + scrollPadding);

  container.scrollTo({ left: container.scrollLeft + delta, behavior: 'smooth' });
  return true;
}
/** @type {Promise<{ items: { variant_id: number, quantity: number }[] }> | null} */
let cartStateRequest = null;

/**
 * Reads the cart, sharing one request across the components a single cart update wakes. The
 * promise is dropped on the next microtask, so a later call still gets fresh data.
 *
 * @returns {Promise<{ items: { variant_id: number, quantity: number }[] }>}
 */
export function fetchCartState() {
  if (!cartStateRequest) {
    cartStateRequest = fetch(`${Theme?.routes?.cart_url || '/cart'}.js`).then((response) => response.json());
    queueMicrotask(() => (cartStateRequest = null));
  }

  return cartStateRequest;
}

/**
 * How many of a variant the cart currently holds.
 *
 * @param {string | number | undefined} variantId
 * @returns {Promise<number>}
 */
export async function cartQuantityForVariant(variantId) {
  if (!variantId) return 0;

  const cart = await fetchCartState();
  return cart.items?.find((item) => String(item.variant_id) === String(variantId))?.quantity ?? 0;
}
